import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { Table, Input, Button, Tag, Image, message, Dropdown, Modal } from "antd";
import {
    SearchOutlined,
    UploadOutlined,
    DownloadOutlined,
    PlusOutlined,
    EyeOutlined,
    FilterOutlined,
    EditOutlined,
    DeleteOutlined,
    MoreOutlined,
} from "@ant-design/icons";
import Layout from '../../Layout'
import useAuthStore from "../../store/authStore";
import * as XLSX from "xlsx";
import FilterPanel from "./FilterPanel";
import useTableColumnSettings from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";
import type { MenuProps } from "antd";
import { useNavigate } from "react-router-dom";

const { Search } = Input;

const data = [
    {
        id: 21593,
        name: "Ultherapy Prime",
        price: "12.222",
        image: "https://picsum.photos/60",
        versions: 1,
        category: "Làm đẹp",
        status: "active",
    },
    {
        id: 21592,
        name: "Liệu trình X-White",
        price: "180.000",
        image: "https://picsum.photos/61",
        versions: 1,
        category: "Làm đẹp",
        status: "active",
    },
];

export default function ProductList() {
  const { t } = useTranslation();
    // Phan quyen thao tac tren san pham
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("products.create");
    const canEdit = isSuperAdmin || hasPermission("products.edit");
    const canDelete = isSuperAdmin || hasPermission("products.delete");
    const canImport = isSuperAdmin || hasPermission("products.import");
    const canExport = isSuperAdmin || hasPermission("products.export");
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [showFilter, setShowFilter] = useState(false);
    const [products, setProducts] = useState(data);
    const navigate = useNavigate();

    const handleEdit = (record: any) => {
        navigate(`/product/add`);
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: t('products.delete_title'),
            content: t('products.delete_confirm'),
            okText: t('products.delete'),
            okType: "danger",
            cancelText: t('products.cancel'),

            onOk() {
                setProducts((prev) => prev.filter((item) => item.id !== id));
                message.success(t('products.deleted'));
            },
        });
    };

    const columns = [
        {
            title: t('products.col_id'),
            dataIndex: "id",
            width: 100,
        },
        {
            title: t('products.col_name'),
            dataIndex: "name",
            render: (text: string) => (
                <span className="text-[#3e91ab] font-medium cursor-pointer">
                    {text}
                </span>
            ),
        },
        {
            title: t('products.col_price'),
            dataIndex: "price",
            width: 140,
        },
        {
            title: t('products.col_image'),
            dataIndex: "image",
            width: 120,
            render: (img: string) => (
                <Image src={img} width={40} height={40} className="rounded" />
            ),
        },
        {
            title: t('products.col_version'),
            dataIndex: "versions",
            width: 120,
        },
        {
            title: t('products.col_category'),
            dataIndex: "category",
            width: 160,
        },
        {
            title: t('products.col_status'),
            dataIndex: "status",
            width: 140,
            render: (status: string) => (
                <Tag color="green">{t('products.status_active')}</Tag>
            ),
        },
        {
            key: "actions",
            title: t('products.col_action'),
            fixed: "right" as const,
            lockVisible: true,
            render: (_: any, record: any) => {

                const items: MenuProps["items"] = [
                    ...(canEdit ? [{
                        key: "edit",
                        icon: <EditOutlined />,
                        label: t('products.edit'),
                        onClick: () => handleEdit(record),
                    }] : []),
                    ...(canDelete ? [{
                        key: "delete",
                        icon: <DeleteOutlined />,
                        label: t('products.delete'),
                        danger: true,
                        onClick: () => handleDelete(record.id),
                    }] : []),
                ];

                return (
                    <Dropdown menu={{ items }} trigger={["click"]}>
                        <Button icon={<MoreOutlined />} />
                    </Dropdown>
                );
            },
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "product_list",
        (canEdit || canDelete) ? columns : columns.filter((c: any) => c.key !== "actions")
    );

    const handleImport = (e: any) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (evt: any) => {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];

            const jsonData = XLSX.utils.sheet_to_json(sheet);

            console.log("Đã nhập:", jsonData);

            message.success(t('products.import_success'));
        };

        reader.readAsArrayBuffer(file);
    };

    const handleExport = () => {
        const worksheet = XLSX.utils.json_to_sheet(data); // dữ liệu bảng của bạn

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Products");

        XLSX.writeFile(workbook, "products.xlsx");
    };

    return (
        <Layout>
            <Helmet>
                <title>{t('page.products')} | Khothi</title>
            </Helmet>
            <div className="flex gap-2 flex-col md:flex-row md:justify-between mb-4 flex justify-between items-center">
                <h2 className="text-lg text-gray-900 font-semibold t-text-white">
                    {t('products.title')}
                </h2>
                <Search
                    placeholder={t('products.search')}
                    className="w-full sm:w-[260px]"
                />
            </div>
            <div className="p-6 bg-white dark:bg-[#141414] rounded-lg mb-4">
                {/* HEADER */}
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">

                    <h2 className="text-lg font-semibold text-000000e0">
                        {t('products.list_title')}
                    </h2>

                    <div className="flex flex-wrap gap-2">

                        {/* Import */}
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            ref={fileInputRef}
                            onChange={handleImport}
                            hidden
                        />

                        {canImport && (
                            <Button
                                icon={<UploadOutlined />}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {t('products.import_excel')}
                            </Button>
                        )}

                        {/* Export */}
                        {canExport && (
                            <Button icon={<DownloadOutlined />} onClick={handleExport}>
                                {t('products.export_excel')}
                            </Button>
                        )}

                        {/* Add */}
                        {canCreate && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => navigate("/product/add")}
                                className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                            >
                                {t('products.add')}
                            </Button>
                        )}

                        {/* Filter */}
                        <Button
                            icon={<FilterOutlined />}
                            onClick={() => setShowFilter(!showFilter)}
                        >
                            {t('products.filter')}
                        </Button>

                        <ColumnSettingButton {...columnSettingProps} />

                    </div>
                </div>
            </div>

            {showFilter && <FilterPanel />}

            {/* TABLE */}

            <Table
                columns={tableColumns}
                components={tableComponents}
                tableLayout={tableLayout}
                dataSource={products}
                rowKey="id"
                scroll={{ x: scrollX }}
                className="custom-ant-table"
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                }}
            />
        </Layout>
    );
}